import { useEffect, useState } from 'react';
import determineBudgetStatus from './determine-budget-status';
import { Budget, BudgetStatus, Env, Transaction } from '../types';
import {
  BANK_CONNECT_SERVICE_BASE_URL,
  BUDGET_SERVICE_BASE_URL,
} from '../config/constants';

const responseToJSON = async (response: Response) => await response.json();

export default function useBudgetStatus(
  token: string,
  environment: Env,
  completionCallback?: () => void
) {
  const [transactions, setTransactions] = useState<Transaction[] | null>(null);
  const [budgets, setBudgets] = useState<Budget[] | null>(null);
  const [registrations, setRegistrations] = useState<[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const budgetServiceUrl = getEnvironmentallyAwareUrl(
    BUDGET_SERVICE_BASE_URL,
    environment
  );

  const bankConnectServiceUrl = getEnvironmentallyAwareUrl(
    BANK_CONNECT_SERVICE_BASE_URL,
    environment
  );

  const budgetsUrl = `${budgetServiceUrl}/budget/budgets`;
  const untrackedTransactionsUrl = `${budgetServiceUrl}/budget/transactions?uncategorized=true`;
  const bankConnectRegistrationsUrl = `${bankConnectServiceUrl}/bank-connect/registrations`;

  const config = {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  };

  useEffect(() => {
    const fetchBudgetData = async () => {
      const budget = await fetch(budgetsUrl, config).then(responseToJSON);
      setBudgets(budget._embedded.budget);

      // return early if budget status can be determined with single API call
      const budgetStatus = determineBudgetStatus(budget._embedded.budget);
      if (budgetStatus !== BudgetStatus.Default) {
        return;
      }

      const [transaction, registration] = await Promise.all([
        fetch(untrackedTransactionsUrl, config).then(responseToJSON),
        fetch(bankConnectRegistrationsUrl, config).then(responseToJSON),
      ]);

      setTransactions(transaction._embedded.transaction);
      setRegistrations(registration._embedded.registration);
    };

    fetchBudgetData()
      .catch(err => setError(err.message))
      .finally(() => {
        setIsLoading(false);
        if (completionCallback) {
          completionCallback();
        }
      });
  }, [token, environment]);

  const budgetStatus = determineBudgetStatus(
    budgets || [],
    transactions,
    registrations
  );

  let data: {
    status: BudgetStatus;
    untrackedTransactions: number;
  } = { status: budgetStatus, untrackedTransactions: 0 };

  if (transactions) {
    data.untrackedTransactions = transactions.length;
  }

  return { data, loading: isLoading, error };
}

function getEnvironmentallyAwareUrl(url: string, environment: Env) {
  if (environment === 'PROD') {
    return url;
  }

  const split = url.split('everydollar');
  split.splice(1, 0, `${environment.toLowerCase()}.everydollar`);

  return split.join('');
}
